import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Home, Sparkles, ShoppingBag, Store, MapPin, ChevronDown, Package, PartyPopper, Heart, Building2, User, LogOut, LayoutDashboard, LogIn, UserPlus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCity } from "@/context/CityContext";
import { useCustomer } from "@/context/CustomerContext";
import { InstallPWA } from "@/components/InstallPWA";
import { InstallAppButton } from "@/components/InstallAppButton";
import Footer from "@/components/Footer";
import logoMark from "@/assets/logo-mark.png";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { to: "/", label: "Home", icon: Home, testid: "nav-home" },
  { to: "/packages", label: "Packages", icon: Package, testid: "nav-packages" },
  { to: "/occasions", label: "Occasions", icon: PartyPopper, testid: "nav-occasions" },
  { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors" },
  { to: "/services", label: "Services", icon: Sparkles, testid: "nav-services" },
  { to: "/halls", label: "Halls", icon: Building2, testid: "nav-halls" },
  { to: "/cart", label: "Cart", icon: ShoppingBag, testid: "nav-cart" },
];

function CitySelector() {
  const { city, setCity, cities } = useCity();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="city-selector"
          className="flex items-center gap-1.5 h-9 px-3 rounded-full border border-stone-300 text-sm font-bold text-[#C2410C] hover:bg-muted transition-colors"
        >
          <MapPin className="h-4 w-4 text-[#C2410C]" />
          <span className="max-w-[90px] truncate">{city || "All nearby"}</span>
          <ChevronDown className="h-3.5 w-3.5 text-[#C2410C]" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto">
        <DropdownMenuItem data-testid="city-option-all" onClick={() => setCity("")}>
          All nearby towns
        </DropdownMenuItem>
        {cities.map((c) => (
          <DropdownMenuItem key={c} data-testid={`city-option-${c}`} onClick={() => setCity(c)}>
            {c}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountMenu() {
  const navigate = useNavigate();
  const { customer, logout } = useCustomer();

  if (!customer) {
    return (
      <div className="flex items-center gap-1.5">
        <button
          data-testid="nav-login"
          onClick={() => navigate("/login")}
          className="h-9 px-3 md:px-4 rounded-full text-sm font-bold text-[#C2410C] hover:bg-muted transition-colors flex items-center gap-1.5"
        >
          <LogIn className="h-4 w-4" /> <span className="hidden sm:inline">Login</span>
        </button>
        <button
          data-testid="nav-register"
          onClick={() => navigate("/register")}
          className="h-9 px-3 md:px-4 rounded-full text-sm font-bold text-white bg-primary hover:bg-primary/90 transition-colors flex items-center gap-1.5"
        >
          <UserPlus className="h-4 w-4" /> <span className="hidden sm:inline">Register</span>
        </button>
      </div>
    );
  }

  const first = (customer.full_name || "You").split(" ")[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="nav-account"
          className="flex items-center gap-1.5 h-9 px-2.5 md:px-3 rounded-full border border-stone-300 text-sm font-bold text-[#C2410C] hover:bg-muted transition-colors"
        >
          <span className="h-6 w-6 rounded-full bg-primary/10 grid place-items-center">
            <User className="h-3.5 w-3.5 text-primary" />
          </span>
          <span className="max-w-[80px] truncate hidden sm:inline">{first}</span>
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem data-testid="nav-account-dashboard" onClick={() => navigate("/account")}>
          <LayoutDashboard className="h-4 w-4 mr-2" /> My Dashboard
        </DropdownMenuItem>
        <DropdownMenuItem data-testid="nav-account-logout" onClick={() => { logout(); navigate("/"); }}>
          <LogOut className="h-4 w-4 mr-2" /> Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useCart();

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <header className="sticky top-0 z-40 glass border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
            <img src={logoMark} alt="Book My Event" className="h-10 md:h-12 w-auto object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-extrabold text-xl md:text-3xl tracking-tight" style={{ fontFamily: "'Baloo 2', sans-serif" }}>
                {[
                  ["B", "#E6337F"], ["o", "#F5911E"], ["o", "#F5911E"], ["k", "#6DBE45"],
                  ["M", "#22B7C6"], ["y", "#7A3FA0"],
                  ["E", "#E6337F"], ["v", "#F5911E"], ["e", "#7A3FA0"], ["n", "#F5911E"], ["t", "#2E9BD6"],
                ].map(([ch, c], i) => <span key={i} style={{ color: c }}>{ch}</span>)}
                <span style={{ color: "#2E9BD6", fontSize: "0.65em" }}>.xyz</span>
              </span>
              <span
                className="text-[9px] md:text-[11px] font-bold tracking-[0.22em] uppercase mt-1 bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(90deg,#ec4899,#f97316,#eab308,#22c55e,#06b6d4,#8b5cf6)" }}
              >
                Tap. Book. Celebrate.
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <CitySelector />
            <AccountMenu />
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((n) => {
                const active = location.pathname === n.to;
                return (
                  <button
                    key={n.to}
                    data-testid={`top-${n.testid}`}
                    onClick={() => navigate(n.to)}
                    className={`px-4 py-2 rounded-full text-sm font-bold text-[#C2410C] transition-colors ${
                      active ? "bg-orange-100" : "hover:bg-muted"
                    }`}
                  >
                    {n.label}
                    {n.to === "/cart" && count > 0 && <span className="ml-1 text-xs">({count})</span>}
                  </button>
                );
              })}
              <button
                data-testid="top-nav-decor"
                onClick={() => navigate("/decorations")}
                className={`px-4 py-2 rounded-full text-sm font-bold text-[#C2410C] flex items-center gap-1.5 transition-colors ${location.pathname === "/decorations" ? "bg-orange-100" : "hover:bg-muted"}`}
              >
                <Heart className="h-4 w-4" /> Decor
              </button>
              <button
                data-testid="top-nav-ai"
                onClick={() => navigate("/ai-planner")}
                className={`px-4 py-2 rounded-full text-sm font-bold text-[#C2410C] flex items-center gap-1.5 transition-colors ${location.pathname === "/ai-planner" ? "bg-orange-100" : "hover:bg-muted"}`}
              >
                <Sparkles className="h-4 w-4" /> AI Planner
              </button>
              <Link
                to="/become-vendor"
                data-testid="top-become-vendor"
                className="ml-1 px-4 py-2 rounded-full text-sm font-bold text-[#C2410C] border border-[#C2410C]/40 hover:bg-orange-100 flex items-center gap-1.5 transition-colors"
              >
                <Store className="h-4 w-4" /> List business
              </Link>
            </nav>
            <InstallAppButton />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto w-full">
        <Outlet />
      </main>

      <Footer />

      <InstallPWA />

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-stone-200/60">
        <div className="flex items-center justify-around h-16">
          {navItems.map((n) => {
            const active = location.pathname === n.to;
            const Icon = n.icon;
            return (
              <button
                key={n.to}
                data-testid={`bottom-${n.testid}`}
                onClick={() => navigate(n.to)}
                className={`relative flex flex-col items-center justify-center gap-0.5 w-16 h-full ${
                  active ? "text-primary" : "text-muted-foreground"
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-semibold">{n.label}</span>
                {n.to === "/cart" && count > 0 && (
                  <span className="absolute top-2 right-3 h-4 min-w-4 px-1 rounded-full bg-primary text-white text-[9px] grid place-items-center">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
